/**
 * TrackOutput — renders both track_monthly and track_quarterly steps. The two
 * share a renderer because their shapes overlap; each field is rendered
 * defensively so a monthly payload (progress_assessment / updated_gap_score /
 * plan_adjustments / next_priorities / sustainability_check) and a quarterly
 * one (market_update / gap_trajectory / domain_trajectory / plan_effectiveness
 * / revised_plan / target_recalibration) both render whatever they carry.
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, SectionLabel } from "./shared";

/** Pick a trend icon from a free-text trend string. */
function TrendIcon({ trend }: { trend?: string }) {
  const theme = useTheme();
  const t = (trend ?? "").toLowerCase();
  if (t.includes("up") || t.includes("improv") || t.includes("better")) {
    return <TrendingUpIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />;
  }
  if (t.includes("down") || t.includes("declin") || t.includes("worse")) {
    return <TrendingDownIcon sx={{ fontSize: 18, color: theme.palette.error.dark }} />;
  }
  return <TrendingFlatIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />;
}

function Prose({ label, text }: { label: string; text?: string }) {
  if (!text) return null;
  return (
    <Box>
      <SectionLabel>{label}</SectionLabel>
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {text}
      </Typography>
    </Box>
  );
}

export default function TrackOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const domainTrajectory: any[] = o.domain_trajectory ?? [];

  return (
    <Stack spacing={2.5}>
      {typeof o.updated_gap_score === "number" && (
        <Chip
          label={t("TrackOutput.updatedReadiness", { value: 100 - o.updated_gap_score })}
          sx={{ alignSelf: "flex-start", fontWeight: 500, bgcolor: alpha(theme.palette.primary.light, 0.1), color: theme.palette.primary.main }}
        />
      )}

      {/* Monthly fields */}
      <Prose label={t("TrackOutput.progressAssessment")} text={o.progress_assessment} />
      <BulletSection label={t("TrackOutput.planAdjustments")} items={o.plan_adjustments} />
      <BulletSection label={t("TrackOutput.nextPriorities")} items={o.next_priorities} />
      <Prose label={t("TrackOutput.sustainabilityCheck")} text={o.sustainability_check} />

      {/* Quarterly fields */}
      <Prose label={t("TrackOutput.marketUpdate")} text={o.market_update} />
      <Prose label={t("TrackOutput.gapTrajectory")} text={o.gap_trajectory} />

      {domainTrajectory.length > 0 && (
        <Box>
          <SectionLabel>{t("TrackOutput.domainTrajectory")}</SectionLabel>
          <Stack spacing={0.75} mt={0.75}>
            {domainTrajectory.map((d, i) => (
              <Stack key={`dt-${i}`} direction="row" spacing={1} alignItems="center">
                <TrendIcon trend={d?.trend} />
                <Typography variant="body2" color="text.secondary">
                  <Typography component="span" fontWeight={500} color="text.primary">
                    {d?.domain ?? t("TrackOutput.domainFallback", { index: i + 1 })}
                  </Typography>
                  {d?.trend ? t("TrackOutput.trendSuffix", { trend: d.trend }) : ""}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <Prose label={t("TrackOutput.planEffectiveness")} text={o.plan_effectiveness} />
      <BulletSection label={t("TrackOutput.revisedPlan")} items={o.revised_plan} />
      <Prose label={t("TrackOutput.targetRecalibration")} text={o.target_recalibration} />

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
